/* eslint-disable react/prop-types */
/**
 * Phase 18 Ask 5 — Detail-page panels for the new analytics result
 * fields. Each panel renders only when its result field is populated;
 * otherwise it returns null (graceful hide).
 *
 *   • BoTracePanel             — line chart of best_pk_so_far per iteration
 *   • AdaptivePolicyPanel      — policy_summary table + delta_pk + sparkline
 *   • NarrowRlPanel            — bar set over the Q-table
 *   • DecisionVariablesPanel   — tabular Parameter Provenance
 */
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
    LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
} from "recharts";
import QTableHeatmap from "./QTableHeatmap";

const fmtPct = (v) =>
    v === null || v === undefined || Number.isNaN(Number(v))
        ? "—"
        : `${(Number(v) * 100).toFixed(1)}%`;

const fmtNum = (v, d = 3) =>
    v === null || v === undefined || Number.isNaN(Number(v))
        ? "—"
        : Number(v).toFixed(d);

/* ──────────────────────────────────────────────────────────────────── */
/* BO trace                                                             */
/* ──────────────────────────────────────────────────────────────────── */
export function BoTracePanel({ tpk }) {
    const trace = tpk?.bo_trace;
    if (!trace || !Array.isArray(trace) || trace.length === 0) return null;
    const data = trace.map((r) => ({
        iter: r.iteration,
        best: Number(r.best_pk_so_far),
        mean: Number(r.mean_pk_this_iteration),
    }));
    return (
        <Card data-testid="unified-detail-bo-trace">
            <CardHeader>
                <CardTitle className="text-base">BO loop trace</CardTitle>
            </CardHeader>
            <CardContent>
                <p className="mb-2 text-xs text-ux-text-3">
                    Per-iteration best Pk envelope for the target-Pk
                    optimiser ({trace.length} iterations).
                </p>
                <div style={{ width: "100%", height: 200 }}>
                    <ResponsiveContainer>
                        <LineChart data={data} margin={{ top: 4, right: 12, left: -8, bottom: 4 }}>
                            <XAxis dataKey="iter" tick={{ fontSize: 10 }}
                                   label={{ value: "iteration", position: "insideBottom", offset: -2, fontSize: 10 }} />
                            <YAxis tick={{ fontSize: 10 }} domain={[0, 1]}
                                   label={{ value: "Pk", angle: -90, position: "insideLeft", fontSize: 10 }} />
                            <Tooltip />
                            <Line type="monotone" dataKey="best" stroke="#16a34a" strokeWidth={2} dot={false}
                                  name="best_pk_so_far" />
                            <Line type="monotone" dataKey="mean" stroke="#94a3b8" strokeWidth={1} dot={false}
                                  name="mean_pk_this_iter" />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            </CardContent>
        </Card>
    );
}

/* ──────────────────────────────────────────────────────────────────── */
/* Adaptive policy (CEM)                                                */
/* ──────────────────────────────────────────────────────────────────── */
export function AdaptivePolicyPanel({ adp }) {
    if (!adp || typeof adp !== "object") return null;
    if (!adp.policy_summary && !adp.training_trace) return null;
    const trace = (adp.training_trace || []).map((r) => ({
        iter: r.iteration,
        best: Number(r.best_pk_so_far),
        mean: Number(r.mean_pk),
    }));
    const summary = adp.policy_summary || [];
    return (
        <Card data-testid="unified-detail-adaptive-policy">
            <CardHeader>
                <CardTitle className="text-base">Adaptive policy (CEM)</CardTitle>
            </CardHeader>
            <CardContent>
                <div className="mb-2 flex flex-wrap gap-x-6 gap-y-1 text-xs">
                    <span data-testid="adp-method">
                        method:{" "}
                        <strong>{adp.training_method || "—"}</strong>
                    </span>
                    <span data-testid="adp-engine">
                        engine-in-loop:{" "}
                        <strong>{adp.engine_in_loop ? "yes" : "no"}</strong>
                    </span>
                    <span data-testid="adp-iters">
                        iterations:{" "}
                        <strong>{adp.training_iterations ?? "—"}</strong>
                    </span>
                    <span data-testid="adp-samples">
                        samples/iter:{" "}
                        <strong>{adp.samples_per_iter ?? "—"}</strong>
                    </span>
                    <span data-testid="adp-converged">
                        converged:{" "}
                        <strong>{adp.converged === true ? "yes" : adp.converged === false ? "no" : "—"}</strong>
                    </span>
                </div>
                <div className="mb-3 grid grid-cols-3 gap-3 text-xs"
                     data-testid="adp-kpis">
                    <KvBlock label="Baseline Pk" value={fmtPct(adp.baseline_mean_pk)} />
                    <KvBlock label="Policy Pk"   value={fmtPct(adp.policy_mean_pk)} />
                    <KvBlock label="ΔPk"         value={fmtPct(adp.delta_pk)} />
                </div>
                {trace.length > 0 && (
                    <div style={{ width: "100%", height: 140 }}
                         data-testid="adp-trace-sparkline">
                        <ResponsiveContainer>
                            <LineChart data={trace} margin={{ top: 2, right: 8, left: -12, bottom: 2 }}>
                                <XAxis dataKey="iter" hide />
                                <YAxis hide domain={[0, 1]} />
                                <Tooltip />
                                <Line type="monotone" dataKey="best" stroke="#2563eb" strokeWidth={2} dot={false} />
                                <Line type="monotone" dataKey="mean" stroke="#94a3b8" strokeWidth={1} dot={false} />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                )}
                {summary.length > 0 && (
                    <div className="mt-3 overflow-x-auto"
                         data-testid="adp-policy-summary">
                        <table className="w-full text-xs">
                            <thead>
                                <tr className="text-ux-text-3">
                                    <th className="px-2 text-left">Variable</th>
                                    <th className="px-2 text-right">Rec. value</th>
                                    <th className="px-2 text-right">Range</th>
                                    <th className="px-2 text-right">σ</th>
                                </tr>
                            </thead>
                            <tbody>
                                {summary.slice(0, 8).map((p) => (
                                    <tr key={p.var_id} data-testid={`adp-row-${p.var_id}`}>
                                        <td className="px-2 py-1">{p.var_id}</td>
                                        <td className="px-2 text-right">{fmtNum(p.recommended_value)}</td>
                                        <td className="px-2 text-right">
                                            {fmtNum(p.recommended_low)} – {fmtNum(p.recommended_high)}
                                        </td>
                                        <td className="px-2 text-right">{fmtNum(p.distribution_std)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
                {adp.limitation && (
                    <p className="mt-2 text-[10px] text-ux-text-3">
                        {adp.limitation}
                    </p>
                )}
            </CardContent>
        </Card>
    );
}

/* ──────────────────────────────────────────────────────────────────── */
/* Narrow-RL (tabular Q-learning)                                       */
/* ──────────────────────────────────────────────────────────────────── */
export function NarrowRlPanel({ rl }) {
    if (!rl || typeof rl !== "object") return null;
    const table = rl.policy_table;
    if (!table || !Array.isArray(table) || table.length === 0) return null;
    return (
        <Card data-testid="unified-detail-narrow-rl">
            <CardHeader>
                <CardTitle className="text-base">Narrow-RL Q-table</CardTitle>
            </CardHeader>
            <CardContent>
                <div className="mb-2 flex flex-wrap gap-x-6 gap-y-1 text-xs">
                    <span data-testid="rl-method">
                        method: <strong>{rl.training_method || "—"}</strong>
                    </span>
                    <span data-testid="rl-engine">
                        engine-in-loop: <strong>{rl.engine_in_loop ? "yes" : "no"}</strong>
                    </span>
                    <span data-testid="rl-states">
                        state: <strong>{rl.state_dim || "—"}</strong>
                    </span>
                    <span data-testid="rl-actions">
                        action: <strong>{rl.action_dim || "—"}</strong>
                    </span>
                </div>
                <div className="mb-3 grid grid-cols-3 gap-3 text-xs"
                     data-testid="rl-kpis">
                    <KvBlock label="Baseline Pk" value={fmtPct(rl.baseline_mean_pk)} />
                    <KvBlock label="Policy Pk"   value={fmtPct(rl.policy_mean_pk)} />
                    <KvBlock label="ΔPk"         value={fmtPct(rl.delta_pk)} />
                </div>
                {/* Phase 18 close-off Ask C — true 2-D SVG heatmap of
                    the Q-table (viridis scale; unvisited cells muted;
                    hover tooltips with q_value + visits). */}
                <div data-testid="rl-q-heatmap">
                    <QTableHeatmap
                        policyTable={table}
                        stateLabels={rl.state_labels}
                        actionLabels={rl.action_labels}
                    />
                </div>
                {rl.best_action_by_state && Object.keys(rl.best_action_by_state).length > 0 && (
                    <div className="mt-3" data-testid="rl-best-actions">
                        <div className="mb-1 text-xs font-medium text-ux-text-2">
                            Best action by state
                        </div>
                        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                            {Object.entries(rl.best_action_by_state).map(([state, info]) => (
                                <div key={state}
                                     className="rounded border border-ux-border-1 bg-ux-surface-1 px-2 py-1 text-xs"
                                     data-testid={`rl-best-${state}`}>
                                    <div className="text-ux-text-3">{state}</div>
                                    <div className="text-ux-text-1">
                                        {typeof info === "object" && info?.action_label
                                            ? `${info.action_label} (Q=${fmtNum(info.q_value, 2)})`
                                            : String(info)}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
                {rl.limitation && (
                    <p className="mt-2 text-[10px] text-ux-text-3">
                        {rl.limitation}
                    </p>
                )}
            </CardContent>
        </Card>
    );
}

/* ──────────────────────────────────────────────────────────────────── */
/* Decision variables (Parameter Provenance, on-page mirror of PDF)     */
/* ──────────────────────────────────────────────────────────────────── */
export function DecisionVariablesPanel({ dv }) {
    if (!dv || !Array.isArray(dv) || dv.length === 0) return null;
    return (
        <Card data-testid="unified-detail-decision-variables">
            <CardHeader>
                <CardTitle className="text-base">Parameter Provenance</CardTitle>
            </CardHeader>
            <CardContent>
                <p className="mb-2 text-xs text-ux-text-3">
                    {dv.length} decision variables tracked
                    {" — "}each carries its source registry, confidence
                    class, citation, and the distribution kind drawn from
                    during the sweep.
                </p>
                <div className="overflow-x-auto" data-testid="dv-table-wrap">
                    <table className="w-full text-xs">
                        <thead>
                            <tr className="text-ux-text-3">
                                <th className="px-2 text-left">Variable</th>
                                <th className="px-2 text-left">Category</th>
                                <th className="px-2 text-right">Lo</th>
                                <th className="px-2 text-right">Hi</th>
                                <th className="px-2 text-right">Default</th>
                                <th className="px-2 text-left">Distribution</th>
                                <th className="px-2 text-left">Confidence</th>
                                <th className="px-2 text-right">N drawn</th>
                            </tr>
                        </thead>
                        <tbody>
                            {dv.map((v) => (
                                <tr key={v.var_id}
                                    data-testid={`dv-row-${v.var_id}`}
                                    className="border-t border-ux-border-1/30">
                                    <td className="px-2 py-1 font-mono text-[11px]">
                                        {v.var_id}
                                    </td>
                                    <td className="px-2">{v.category}</td>
                                    <td className="px-2 text-right">{fmtNum(v.lo, 2)}</td>
                                    <td className="px-2 text-right">{fmtNum(v.hi, 2)}</td>
                                    <td className="px-2 text-right">{fmtNum(v.default, 2)}</td>
                                    <td className="px-2">{v.distribution_kind || "—"}</td>
                                    <td className="px-2">
                                        <ConfidenceBadge confidence={v.confidence} />
                                    </td>
                                    <td className="px-2 text-right">{v.n_samples_drawn || 0}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <p className="mt-2 text-[10px] text-ux-text-3">
                    Source: {dv[0].source || "—"} · Citation: {dv[0].citation || "—"}
                </p>
            </CardContent>
        </Card>
    );
}

function KvBlock({ label, value }) {
    return (
        <div className="rounded border border-ux-border-1 bg-ux-surface-1 px-2 py-1">
            <div className="text-[10px] uppercase tracking-wide text-ux-text-3">{label}</div>
            <div className="text-sm text-ux-text-1">{value}</div>
        </div>
    );
}

function ConfidenceBadge({ confidence }) {
    const cls = {
        validated:   "border-green-500/40 bg-green-500/10 text-green-300",
        provisional: "border-amber-500/40 bg-amber-500/10 text-amber-300",
        unverified:  "border-red-500/40   bg-red-500/10   text-red-300",
    }[confidence] || "border-ux-border-1 text-ux-text-3";
    return (
        <span className={`rounded border px-1.5 py-0.5 text-[10px] ${cls}`}>
            {confidence || "—"}
        </span>
    );
}
